import { useState, useCallback, useRef } from 'react'
import { productsApi } from '../services/api'

/**
 * Chargement du catalogue.
 *
 * Deux corrections structurelles :
 *
 * 1. `fetch` prend les paramètres COMPLETS, sans fusion avec l'état
 *    précédent. La fusion faisait persister l'ancienne valeur quand on
 *    retirait un filtre : on se retrouvait avec deux produits affichés sur
 *    un catalogue entier.
 *
 * 2. Le hook ne lance plus de requête au montage. Il en lançait une avec
 *    `initialParams` pendant que la page en lançait une autre avec ses
 *    filtres réels : deux `GET /products` concurrents, sans annulation ni
 *    ordre garanti. Sur une connexion lente, la requête sans filtre pouvait
 *    résoudre en dernier et écraser le résultat filtré — un client arrivant
 *    sur `/catalogue?category=salons` voyait tout le catalogue.
 *
 * Un jeton de séquence garantit qu'une réponse tardive n'écrase jamais une
 * plus récente.
 */
export function useProducts() {
  const [products, setProducts] = useState([])
  const [meta,     setMeta]     = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  const sequence = useRef(0)

  const fetch = useCallback((params = {}) => {
    const current = ++sequence.current

    setLoading(true)
    setError(null)

    return productsApi.list(params)
      .then(res => {
        // Une requête plus récente a été lancée : cette réponse est périmée.
        if (current !== sequence.current) return

        setProducts(res.data ?? [])
        setMeta(res.meta ?? null)
      })
      .catch(err => {
        if (current !== sequence.current) return

        setError(err)
        setProducts([])
      })
      .finally(() => {
        if (current === sequence.current) setLoading(false)
      })
  }, [])

  return { products, meta, loading, error, fetch }
}
